import {Spin} from 'antd';
import {FormInstance} from 'antd/es/form/Form';
import React, {Key, useCallback, useEffect, useState} from 'react';

import {useCustomTable} from 'components/@common/widgets/custom-table/custom-table';
import {selectContextRawData} from 'modules/context/context-selector';
import {usePagination} from 'shared/hooks/use-pagination';
import {showMessageFromResponse} from 'shared/utils';
import {useAppSelector} from 'store/config/hooks';

import {IGroupCompanyDto, IPostDataGroupCompanyArgs} from '../../../api/types';
import {useCompanyGroupInfoColumns} from '../../../hooks/use-group-info-columns';
import {GroupCompanyTabTabService} from '../../../services';
import {ActionPanel} from '../../action-panel';
import {GroupCompanyInfoForm} from '../../forms/group-company-form';

export const GroupCompanyInfoTab = () => {
    const [tableData, setTableData] = useState<IGroupCompanyDto[]>([]);

    const [selectedRowKeys, setSelectedRowKeys] = useState<Key[]>([]);

    const [isLoadingTableData, setIsLoadingTableData] = useState<boolean>(false);

    const [totalRows, setTotalRows] = useState<number>(0);

    const [currentPage, setCurrentPage] = useState<number>(1);

    const {pageSize, handleChange} = usePagination({pageDefault: 1, pageSizeDefault: 10});
    const {organizationId} = useAppSelector(selectContextRawData) ?? {};

    const fetchData = useCallback(async () => {
        try {
            setIsLoadingTableData(true);
            const {data} = await GroupCompanyTabTabService.get({
                paginationPageSize: pageSize,
                paginationCurrent: currentPage,
            });
            setTableData(data.dataTable);
            setTotalRows(data.recordsTotal);
            setIsLoadingTableData(false);
        } catch (e) {
            console.error('Ошибка получения записей: ', e);
        }
    }, [pageSize, currentPage]);

    const columns = useCompanyGroupInfoColumns(tableData, fetchData);

    useEffect(() => {
        fetchData();
    }, [organizationId]);

    const {customTableJSX} = useCustomTable({
        rowKey: 'id',
        pagination: {
            position: ['bottomLeft'],
            total: (() => totalRows)(),
            pageSize,
            onChange: (value: number) => {
                handleChange(value);
                setCurrentPage(value);
            },
        },
        rowSelection: {selectedRowKeys, onChange: (selectedTableRowKeys) => setSelectedRowKeys(selectedTableRowKeys)},
        className: 'mt-5',
        dataSource: tableData,
        bordered: true,
        columns,
    });

    const handleDeleteRows = useCallback(async (rowsIds: Key[]) => {
        try {
            await GroupCompanyTabTabService.delete({rowsIds});
            setSelectedRowKeys([]);
            await fetchData();
        } catch (e) {
            console.error(e);
        }
    }, []);

    const handleFinish = useCallback(async (values: IPostDataGroupCompanyArgs) => {
        try {
            const {response, isError} = await GroupCompanyTabTabService.post(values);

            if (isError) {
                showMessageFromResponse({response, isError});
            }

            await fetchData();
        } catch (e) {
            console.error(e);
        }
    }, []);

    return (
        <>
            <ActionPanel<IPostDataGroupCompanyArgs>
                config={{
                    selectedRowKeys,
                    handleDeleteRows,
                    handleFinish,
                    title: 'Сведения по структуре группы компаний',
                }}
            >
                {(formInstance: FormInstance, onFinish?: (values: IPostDataGroupCompanyArgs) => Promise<void>) => (
                    <GroupCompanyInfoForm<IPostDataGroupCompanyArgs>
                        formInstance={formInstance}
                        onFinish={onFinish}
                    />
                )}
            </ActionPanel>
            <Spin spinning={isLoadingTableData}>{customTableJSX}</Spin>
        </>
    );
};
